import { Component, h, escapeHtml, uid, register, registerFieldType } from './core.js';
import { renderIcon } from './icons.js';
import { treeLayout, layeredLayout, mindMapLayout } from './layout.js';
import { ContextMenu } from './menu.js';

/*
 * Diagram: auto-laid-out node and connector diagrams (hierarchies, org charts,
 * dependency graphs, mind maps) with pan, zoom, fit, selection and optional
 * editing. Nodes are HTML, connectors are SVG.
 *
 * Options
 *   nodes: [{ id, width, height, data, shape, style, annotations, offsetX, offsetY }]
 *     shape: { type: 'Basic', shape: 'Rectangle' | 'Ellipse' | 'Diamond', cornerRadius }
 *          | { type: 'HTML', content: string | (node) => string }
 *     style: { fill, strokeColor, strokeWidth, strokeDashArray }
 *     annotations: [{ content, style: { color, bold, fontSize, fill }, offset: { x, y },
 *                     horizontalAlignment, verticalAlignment, margin }]
 *   connectors: [{ id, sourceID, targetID, type: 'Orthogonal' | 'Straight' | 'Bezier',
 *                  style: { strokeColor, strokeWidth, strokeDashArray },
 *                  sourceDecorator | targetDecorator: { shape: 'Arrow' | 'Circle' | 'None', width, height, style },
 *                  annotations: [{ content, style, alignment: 'Center' | 'Before' | 'After' }] }]
 *   dataSourceSettings: { id, parentId, dataSource: [], root }   flat parent/child data instead of nodes + connectors
 *   layout: { type: 'HierarchicalTree' | 'OrganizationalChart' | 'ComplexHierarchicalTree' | 'MindMap' | 'None',
 *             orientation, horizontalSpacing, verticalSpacing, margin, getBranch, root }
 *   getNodeDefaults(node), getConnectorDefaults(connector, diagram)   hooks applied to every item
 *   interaction: { pan: true, zoom: true, select: false, drag: false, edit: false }
 *   tool: 'ZoomPan' | 'None' | 'SingleSelect'   shorthand for the interaction flags
 *   toolbar: true          zoom in / zoom out / fit buttons in the corner
 *   fitOnRender: true, minZoom: 0.1, maxZoom: 4, background: null
 *   nodeDefaults: { width, height, fill, strokeColor, strokeWidth, color, fontSize }
 *   connectorDefaults: { type, strokeColor, strokeWidth, targetDecorator }
 * Value: { nodes, connectors } (positions included).
 * Events: click { node, connector, event }, doubleClick, selectionChange { node }, textEdit { node, text },
 *         nodeAdded { node, connector }, nodeRemoved { ids }, nodeMoved { node }, zoom { zoom }, valueChanged.
 */

const rid = (p) => uid(p) + '-' + Math.random().toString(36).slice(2, 7);

export class Diagram extends Component {
	static get componentName() { return 'diagram'; }

	defaults() {
		return {
			nodes: [], connectors: [], dataSourceSettings: null, value: null,
			layout: { type: 'HierarchicalTree', orientation: 'TopToBottom', horizontalSpacing: 30, verticalSpacing: 50, margin: { left: 40, top: 40, right: 40, bottom: 40 }, getBranch: null, root: null },
			getNodeDefaults: null, getConnectorDefaults: null,
			interaction: null, tool: null, toolbar: true, fitOnRender: true, minZoom: 0.05, maxZoom: 4, background: null,
			nodeDefaults: { width: 120, height: 40, fill: '#6ba5d7', strokeColor: '#ffffff', strokeWidth: 1, color: '#ffffff', fontSize: 12 },
			connectorDefaults: { type: 'Orthogonal', strokeColor: '#94a3b8', strokeWidth: 1.5, targetDecorator: 'Arrow' },
			emptyText: 'Nothing to show.',
			// contextMenu: true (built-in items in edit mode), an item array, or (ctx) => items. ctx is { node, connector, diagram, defaults }.
			contextMenu: true,
			// Handles shown on the selected node in edit mode: 'add' and/or 'delete'.
			handles: ['add', 'delete'],
			colours: ['#E74C3C', '#F39C12', '#F1C40F', '#2ECC71', '#1ABC9C', '#3498DB', '#8E44AD', '#E91E63', '#6B7280', '#1E293B'],
		};
	}

	_interaction() {
		const o = this.options;
		const base = { pan: true, zoom: true, select: false, drag: false, edit: false };
		if (o.tool === 'None') Object.assign(base, { pan: true, zoom: true });
		if (o.tool === 'SingleSelect') Object.assign(base, { select: true });
		return Object.assign(base, o.interaction || {});
	}

	// ------------------------------------------------------------ render
	render() {
		const o = this.options;
		this.el.className = 'dfc dfc-diagram' + (o.className ? ' ' + o.className : '');
		if (o.background) this.el.style.background = o.background;
		if (!this.viewport) {
			this.edgeLayer = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
			this.edgeLayer.setAttribute('class', 'dfc-diagram-edges');
			this.nodeLayer = h('div', { className: 'dfc-diagram-nodes' });
			this.viewport = h('div', { className: 'dfc-diagram-viewport' }, this.edgeLayer, this.nodeLayer);
			this.el.appendChild(this.viewport);
			this.emptyEl = h('div', { className: 'dfc-diagram-empty', text: o.emptyText, hidden: true });
			this.el.appendChild(this.emptyEl);
			if (o.toolbar) {
				this.toolbarEl = h('div', { className: 'dfc-diagram-toolbar' },
					h('button', { type: 'button', className: 'dfc-diagram-tool', title: 'Zoom in', 'aria-label': 'Zoom in', onClick: () => this.zoomIn() }, renderIcon('plus')),
					h('button', { type: 'button', className: 'dfc-diagram-tool', title: 'Zoom out', 'aria-label': 'Zoom out', onClick: () => this.zoomOut() }, renderIcon('minus')),
					h('button', { type: 'button', className: 'dfc-diagram-tool', title: 'Fit to view', 'aria-label': 'Fit to view', onClick: () => this.fitToPage() }, renderIcon('fit')));
				this.el.appendChild(this.toolbarEl);
			}
			this._zoom = 1; this._tx = 0; this._ty = 0;
		}
		this._build();
		this.doLayout(o.fitOnRender);
	}

	/** Normalises option data into internal node and connector records. */
	_build() {
		const o = this.options;
		const src = o.value || {};
		let nodes = src.nodes || o.nodes || [], connectors = src.connectors || o.connectors || [];
		const ds = o.dataSourceSettings;
		if (ds && ds.dataSource) {
			const arr = Array.isArray(ds.dataSource) ? ds.dataSource : (ds.dataSource.dataSource || ds.dataSource.json || []);
			const idKey = ds.id || 'id', pKey = ds.parentId || 'parentId';
			nodes = arr.map((d) => ({ id: String(d[idKey]), data: d }));
			connectors = arr.filter((d) => d[pKey] !== undefined && d[pKey] !== null && d[pKey] !== '').map((d) => ({ id: 'e-' + d[pKey] + '-' + d[idKey], sourceID: String(d[pKey]), targetID: String(d[idKey]) }));
			if (ds.root) o.layout.root = String(ds.root);
		}
		this._nodes = nodes.map((raw) => this._makeNode(raw));
		this._byId = new Map(this._nodes.map((n) => [n.id, n]));
		this._connectors = connectors.map((raw) => this._makeConnector(raw)).filter((c) => this._byId.has(c.sourceID) && this._byId.has(c.targetID));
	}
	_makeNode(raw) {
		const d = this.options.nodeDefaults;
		const n = Object.assign({}, raw);
		n.id = String(raw.id !== undefined ? raw.id : rid('node'));
		n.data = raw.data !== undefined ? raw.data : raw;
		n.annotations = (raw.annotations || []).map((a) => Object.assign({}, a, { style: Object.assign({}, a.style || {}) }));
		n.style = Object.assign({}, raw.style || {});
		n.shape = raw.shape ? Object.assign({}, raw.shape) : { type: 'Basic', shape: 'Rectangle' };
		n.ports = raw.ports || [];
		if (typeof this.options.getNodeDefaults === 'function') { const r = this.options.getNodeDefaults(n, this); if (r) Object.assign(n, r); }
		n.width = Number(n.width) || (n.minWidth ? Number(n.minWidth) : d.width);
		n.height = Number(n.height) || d.height;
		if (n.offsetX !== undefined) n.x = Number(n.offsetX);
		if (n.offsetY !== undefined) n.y = Number(n.offsetY);
		return n;
	}
	_makeConnector(raw) {
		const c = Object.assign({}, raw);
		c.id = String(raw.id !== undefined ? raw.id : rid('edge'));
		c.sourceID = String(raw.sourceID !== undefined ? raw.sourceID : raw.source);
		c.targetID = String(raw.targetID !== undefined ? raw.targetID : raw.target);
		c.style = Object.assign({}, raw.style || {});
		c.annotations = (raw.annotations || []).map((a) => Object.assign({}, a));
		if (typeof this.options.getConnectorDefaults === 'function') { const r = this.options.getConnectorDefaults(c, this); if (r) Object.assign(c, r); }
		return c;
	}

	// ------------------------------------------------------------ layout
	get _vertical() { const or = (this.options.layout || {}).orientation || 'TopToBottom'; return or === 'TopToBottom' || or === 'BottomToTop' || or === 'Vertical'; }

	/** Runs the configured layout, then redraws. */
	doLayout(fit) {
		const lay = Object.assign({ type: 'HierarchicalTree', orientation: 'TopToBottom' }, this.options.layout || {});
		// Labelled connectors need room between the layers, or the label lands on a node.
		const labelWidth = this._maxEdgeLabelWidth();
		if (labelWidth) {
			const need = labelWidth + 28;
			if (this._vertical) lay.verticalSpacing = Math.max(lay.verticalSpacing === undefined ? 50 : lay.verticalSpacing, need);
			else lay.horizontalSpacing = Math.max(lay.horizontalSpacing === undefined ? 30 : lay.horizontalSpacing, need);
		}
		const graph = { nodes: this._nodes, edges: this._connectors.map((c) => ({ source: c.sourceID, target: c.targetID })) };
		let result = null;
		// A "tree" whose data turns out to have nodes with several parents lays out better as a layered graph.
		const inCount = new Map(); graph.edges.forEach((e) => inCount.set(e.target, (inCount.get(e.target) || 0) + 1));
		const multiParent = [...inCount.values()].some((n) => n > 1);
		if (lay.type === 'ComplexHierarchicalTree' || (lay.type === 'HierarchicalTree' && multiParent)) result = layeredLayout(graph, lay);
		else if (lay.type === 'MindMap') { result = mindMapLayout(graph, lay); this._branches = result.branchOf; }
		else if (lay.type !== 'None') result = treeLayout(graph, lay);
		if (result) {
			this._layers = result.layers;
			this._nodes.forEach((n) => { const p = result.positions.get(n.id); if (p) { n.x = p.x; n.y = p.y; } });
			if (this._branches) this._nodes.forEach((n) => { n.branch = this._branches.get(n.id); if (n.data && typeof n.data === 'object' && !n.data.branch) n.data.branch = n.branch; });
		}
		this._nodes.forEach((n) => { if (n.x === undefined) { n.x = n.width / 2; n.y = n.height / 2; } });
		this._normalise();
		this._draw();
		if (fit) this.fitToPage();
		return this;
	}
	/** Widest connector label in pixels (0 when none), measured in the label font. */
	_maxEdgeLabelWidth() {
		let max = 0;
		const canvas = this._measureCanvas || (this._measureCanvas = document.createElement('canvas'));
		const ctx = canvas.getContext('2d');
		this._connectors.forEach((e) => e.annotations.forEach((a) => {
			if (a.content === undefined || a.content === null || a.content === '') return;
			const s = a.style || {};
			ctx.font = `${s.bold ? '700' : '400'} ${s.fontSize || 10}px system-ui, sans-serif`;
			max = Math.max(max, ctx.measureText(String(a.content)).width + 12);
		}));
		return Math.ceil(max);
	}
	/** Shifts everything so the content starts at the layout margin, and records the content size. */
	_normalise() {
		const m = Object.assign({ left: 40, top: 40, right: 40, bottom: 40 }, (this.options.layout || {}).margin || {});
		if (!this._nodes.length) { this._content = { w: 0, h: 0 }; return; }
		let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
		this._nodes.forEach((n) => { minX = Math.min(minX, n.x - n.width / 2); minY = Math.min(minY, n.y - n.height / 2); maxX = Math.max(maxX, n.x + n.width / 2); maxY = Math.max(maxY, n.y + n.height / 2); });
		const dx = m.left - minX, dy = m.top - minY;
		this._nodes.forEach((n) => { n.x += dx; n.y += dy; });
		this._content = { w: maxX - minX + m.left + m.right, h: maxY - minY + m.top + m.bottom };
	}

	// ------------------------------------------------------------ drawing
	_draw() {
		this.nodeLayer.innerHTML = '';
		this.edgeLayer.innerHTML = '';
		this.emptyEl.hidden = this._nodes.length > 0;
		const c = this._content || { w: 0, h: 0 };
		this.edgeLayer.setAttribute('width', Math.max(1, Math.ceil(c.w)));
		this.edgeLayer.setAttribute('height', Math.max(1, Math.ceil(c.h)));
		this.viewport.style.width = c.w + 'px'; this.viewport.style.height = c.h + 'px';
		this._nodes.forEach((n) => { n.el = this._renderNode(n); this.nodeLayer.appendChild(n.el); });
		this._connectors.forEach((e) => this._renderConnector(e));
		if (this._selected && this._byId.has(this._selected)) this._applySelection();
		this._applyTransform();
	}
	_renderNode(n) {
		const d = this.options.nodeDefaults;
		const el = h('div', { className: 'dfc-diagram-node', dataset: { id: n.id }, style: { left: (n.x - n.width / 2) + 'px', top: (n.y - n.height / 2) + 'px', width: n.width + 'px', height: n.height + 'px' } });
		const st = n.style, shape = n.shape || {};
		const kind = shape.type === 'HTML' ? 'HTML' : (shape.shape || 'Rectangle');
		if (kind === 'HTML') {
			el.classList.add('is-html');
			const content = typeof shape.content === 'function' ? shape.content(n.data, n) : shape.content;
			el.innerHTML = content || '';
		} else if (kind === 'Diamond') {
			el.classList.add('is-diamond');
			const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
			svg.setAttribute('viewBox', `0 0 ${n.width} ${n.height}`); svg.setAttribute('preserveAspectRatio', 'none'); svg.setAttribute('class', 'dfc-diagram-shape');
			const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
			const sw = st.strokeWidth === undefined ? d.strokeWidth : st.strokeWidth;
			poly.setAttribute('points', `${n.width / 2},${sw} ${n.width - sw},${n.height / 2} ${n.width / 2},${n.height - sw} ${sw},${n.height / 2}`);
			poly.setAttribute('fill', st.fill || d.fill); poly.setAttribute('stroke', st.strokeColor || d.strokeColor); poly.setAttribute('stroke-width', sw);
			if (st.strokeDashArray) poly.setAttribute('stroke-dasharray', String(st.strokeDashArray).replace(/,/g, ' '));
			svg.appendChild(poly); el.appendChild(svg);
		} else {
			const sw = st.strokeWidth === undefined ? d.strokeWidth : st.strokeWidth;
			el.style.background = st.fill || d.fill;
			el.style.border = `${sw}px ${st.strokeDashArray ? 'dashed' : 'solid'} ${st.strokeColor || d.strokeColor}`;
			if (st.fill === 'transparent' || st.fill === 'none') el.style.background = 'transparent';
			if (!sw || st.strokeColor === 'none') el.style.border = 'none';
			el.style.borderRadius = kind === 'Ellipse' ? '50%' : (shape.cornerRadius !== undefined ? shape.cornerRadius + 'px' : '4px');
		}
		n.annotations.forEach((a, i) => el.appendChild(this._renderAnnotation(n, a, i)));
		return el;
	}
	_renderAnnotation(n, a, index) {
		const d = this.options.nodeDefaults;
		const s = a.style || {};
		const off = Object.assign({ x: 0.5, y: 0.5 }, a.offset || {});
		const label = h('div', { className: 'dfc-diagram-label', dataset: { index: String(index) } });
		label.innerHTML = escapeHtml(a.content === undefined || a.content === null ? '' : String(a.content)).replace(/\n/g, '<br>');
		label.style.left = (off.x * 100) + '%'; label.style.top = (off.y * 100) + '%';
		const ha = a.horizontalAlignment || 'Center', va = a.verticalAlignment || 'Center';
		label.style.transform = `translate(${ha === 'Left' ? '0' : ha === 'Right' ? '-100%' : '-50%'}, ${va === 'Top' ? '0' : va === 'Bottom' ? '-100%' : '-50%'})`;
		const m = a.margin || {};
		if (m.left || m.top || m.right || m.bottom) label.style.margin = `${m.top || 0}px ${m.right || 0}px ${m.bottom || 0}px ${m.left || 0}px`;
		label.style.color = s.color || (n.shape && n.shape.type === 'HTML' ? 'inherit' : d.color);
		label.style.fontSize = (s.fontSize || d.fontSize) + 'px';
		if (s.bold) label.style.fontWeight = '700';
		if (s.fill) { label.style.background = s.fill; label.style.padding = '1px 4px'; label.style.borderRadius = '3px'; }
		if (index === 0) label.classList.add('is-primary');
		return label;
	}

	_anchor(n, side) {
		if (side === 'top') return { x: n.x, y: n.y - n.height / 2 };
		if (side === 'bottom') return { x: n.x, y: n.y + n.height / 2 };
		if (side === 'left') return { x: n.x - n.width / 2, y: n.y };
		return { x: n.x + n.width / 2, y: n.y };
	}
	/** Computes the polyline (or bezier) for a connector. */
	_route(e) {
		const s = this._byId.get(e.sourceID), t = this._byId.get(e.targetID);
		const type = e.type || this.options.connectorDefaults.type || 'Orthogonal';
		const lay = this.options.layout || {};
		const vertical = lay.type === 'MindMap' ? false : lay.type === 'None' ? Math.abs(t.y - s.y) >= Math.abs(t.x - s.x) : this._vertical;
		if (type === 'Straight') {
			const p1 = this._clip(s, t), p2 = this._clip(t, s);
			return { points: [p1, p2] };
		}
		if (type === 'Bezier') {
			const goRight = t.x >= s.x;
			const p1 = this._anchor(s, vertical ? (t.y >= s.y ? 'bottom' : 'top') : (goRight ? 'right' : 'left'));
			const p2 = this._anchor(t, vertical ? (t.y >= s.y ? 'top' : 'bottom') : (goRight ? 'left' : 'right'));
			const dx = (p2.x - p1.x) / 2, dy = (p2.y - p1.y) / 2;
			const c1 = vertical ? { x: p1.x, y: p1.y + dy } : { x: p1.x + dx, y: p1.y };
			const c2 = vertical ? { x: p2.x, y: p2.y - dy } : { x: p2.x - dx, y: p2.y };
			return { points: [p1, p2], bezier: [c1, c2] };
		}
		// Orthogonal
		const gap = 24;
		if (vertical) {
			const down = t.y - t.height / 2 >= s.y + s.height / 2 - 1;
			const up = t.y + t.height / 2 <= s.y - s.height / 2 + 1;
			if (down || up) {
				const p1 = this._anchor(s, down ? 'bottom' : 'top'), p2 = this._anchor(t, down ? 'top' : 'bottom');
				if (Math.abs(p1.x - p2.x) < 1) return { points: [p1, p2] };
				const midY = (p1.y + p2.y) / 2;
				return { points: [p1, { x: p1.x, y: midY }, { x: p2.x, y: midY }, p2] };
			}
			// Same band: loop around the side.
			const right = t.x >= s.x;
			const p1 = this._anchor(s, right ? 'right' : 'left'), p2 = this._anchor(t, right ? 'left' : 'right');
			if (right ? p2.x - p1.x > gap : p1.x - p2.x > gap) { const midX = (p1.x + p2.x) / 2; return { points: [p1, { x: midX, y: p1.y }, { x: midX, y: p2.y }, p2] }; }
			const outX = right ? Math.max(s.x + s.width / 2, t.x + t.width / 2) + gap : Math.min(s.x - s.width / 2, t.x - t.width / 2) - gap;
			const q2 = this._anchor(t, right ? 'right' : 'left');
			return { points: [p1, { x: outX, y: p1.y }, { x: outX, y: q2.y }, q2] };
		}
		const fwd = t.x - t.width / 2 >= s.x + s.width / 2 - 1;
		const back = t.x + t.width / 2 <= s.x - s.width / 2 + 1;
		if (fwd || back) {
			const p1 = this._anchor(s, fwd ? 'right' : 'left'), p2 = this._anchor(t, fwd ? 'left' : 'right');
			if (Math.abs(p1.y - p2.y) < 1) return { points: [p1, p2] };
			const midX = (p1.x + p2.x) / 2;
			return { points: [p1, { x: midX, y: p1.y }, { x: midX, y: p2.y }, p2] };
		}
		const below = t.y >= s.y;
		const p1 = this._anchor(s, below ? 'bottom' : 'top'), p2 = this._anchor(t, below ? 'top' : 'bottom');
		if (below ? p2.y - p1.y > gap : p1.y - p2.y > gap) { const midY = (p1.y + p2.y) / 2; return { points: [p1, { x: p1.x, y: midY }, { x: p2.x, y: midY }, p2] }; }
		const outY = below ? Math.max(s.y + s.height / 2, t.y + t.height / 2) + gap : Math.min(s.y - s.height / 2, t.y - t.height / 2) - gap;
		const q2 = this._anchor(t, below ? 'bottom' : 'top');
		return { points: [p1, { x: p1.x, y: outY }, { x: q2.x, y: outY }, q2] };
	}
	/** Point on the border of rectangle a towards the centre of b. */
	_clip(a, b) {
		const dx = b.x - a.x, dy = b.y - a.y;
		if (!dx && !dy) return { x: a.x, y: a.y };
		const sx = Math.abs(dx) > 0 ? (a.width / 2) / Math.abs(dx) : Infinity, sy = Math.abs(dy) > 0 ? (a.height / 2) / Math.abs(dy) : Infinity;
		const k = Math.min(sx, sy);
		return { x: a.x + dx * k, y: a.y + dy * k };
	}
	_renderConnector(e) {
		const d = this.options.connectorDefaults;
		const r = this._route(e);
		const pts = r.points;
		const ns = 'http://www.w3.org/2000/svg';
		const g = document.createElementNS(ns, 'g'); g.setAttribute('class', 'dfc-diagram-edge'); g.setAttribute('data-id', e.id);
		const path = document.createElementNS(ns, 'path');
		let dAttr;
		if (r.bezier) dAttr = `M${pts[0].x},${pts[0].y} C${r.bezier[0].x},${r.bezier[0].y} ${r.bezier[1].x},${r.bezier[1].y} ${pts[1].x},${pts[1].y}`;
		else dAttr = pts.map((p, i) => (i ? 'L' : 'M') + p.x + ',' + p.y).join(' ');
		const stroke = e.style.strokeColor || d.strokeColor, width = e.style.strokeWidth === undefined ? d.strokeWidth : e.style.strokeWidth;
		path.setAttribute('d', dAttr); path.setAttribute('fill', 'none'); path.setAttribute('stroke', stroke); path.setAttribute('stroke-width', width);
		if (e.style.strokeDashArray) path.setAttribute('stroke-dasharray', String(e.style.strokeDashArray).replace(/,/g, ' '));
		g.appendChild(path);
		// Wider invisible hit area for clicks.
		const hit = path.cloneNode(); hit.setAttribute('stroke', 'transparent'); hit.setAttribute('stroke-width', Math.max(10, width + 8)); hit.setAttribute('stroke-dasharray', ''); hit.setAttribute('class', 'dfc-diagram-edge-hit');
		g.appendChild(hit);
		const tDec = e.targetDecorator === undefined ? { shape: d.targetDecorator } : (typeof e.targetDecorator === 'string' ? { shape: e.targetDecorator } : e.targetDecorator);
		const sDec = typeof e.sourceDecorator === 'string' ? { shape: e.sourceDecorator } : e.sourceDecorator;
		const endDir = r.bezier ? { from: r.bezier[1], to: pts[1] } : { from: pts[pts.length - 2], to: pts[pts.length - 1] };
		const startDir = r.bezier ? { from: r.bezier[0], to: pts[0] } : { from: pts[1], to: pts[0] };
		if (tDec && tDec.shape && tDec.shape !== 'None') g.appendChild(this._decorator(tDec, endDir, stroke));
		if (sDec && sDec.shape && sDec.shape !== 'None') g.appendChild(this._decorator(sDec, startDir, stroke));
		this.edgeLayer.appendChild(g);
		e.el = g;
		e.annotations.forEach((a) => {
			if (a.content === undefined || a.content === null || a.content === '') return;
			const s = a.style || {};
			let p, side = 'center';
			if (r.bezier) p = this._bezierPoint(pts[0], r.bezier[0], r.bezier[1], pts[1], a.alignment === 'After' ? 0.78 : a.alignment === 'Before' ? 0.22 : 0.5);
			else {
				// Sit the label beside a long segment rather than on an elbow or the arrowhead: the last (After) or first
				// (Before) segment when it has room, otherwise the longest one. Horizontal segments take the label above,
				// vertical ones take it to the right, so it never covers the line.
				const segs = []; for (let i = 1; i < pts.length; i++) segs.push({ a: pts[i - 1], b: pts[i], len: Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y) });
				// A run has to be long enough to hold the label along its direction (its width on a horizontal run, its
				// height on a vertical one) or the label spills onto a node; otherwise fall back to the middle run.
				const canvas = this._measureCanvas || (this._measureCanvas = document.createElement('canvas'));
				const ctx = canvas.getContext('2d'); ctx.font = `${s.bold ? '700' : '400'} ${s.fontSize || 10}px system-ui, sans-serif`;
				const labelW = ctx.measureText(String(a.content)).width + 12, labelH = (s.fontSize || 10) + 6;
				const fits = (sg) => sg.len >= (Math.abs(sg.b.x - sg.a.x) >= Math.abs(sg.b.y - sg.a.y) ? labelW : labelH) + 8;
				const roomy = segs.filter(fits);
				let seg;
				if (a.alignment === 'After' && fits(segs[segs.length - 1])) seg = segs[segs.length - 1];
				else if (a.alignment === 'Before' && fits(segs[0])) seg = segs[0];
				else if (roomy.length) seg = roomy.reduce((m, sg) => (sg.len > m.len ? sg : m), roomy[0]);
				else seg = segs[Math.floor(segs.length / 2)];
				p = { x: (seg.a.x + seg.b.x) / 2, y: (seg.a.y + seg.b.y) / 2 };
			}
			const label = h('div', { className: 'dfc-diagram-edge-label is-' + side, dataset: { edge: e.id }, style: { left: p.x + 'px', top: p.y + 'px', color: s.color || '#64748b', fontSize: (s.fontSize || 10) + 'px', fontWeight: s.bold ? '700' : '400', background: s.fill || 'rgba(255,255,255,0.9)' } });
			label.textContent = String(a.content);
			this.nodeLayer.appendChild(label);
		});
	}
	_decorator(dec, dir, stroke) {
		const ns = 'http://www.w3.org/2000/svg';
		const w = dec.width || 10, hgt = dec.height || 10;
		const ang = Math.atan2(dir.to.y - dir.from.y, dir.to.x - dir.from.x);
		const fill = (dec.style && dec.style.fill) || stroke, sc = (dec.style && dec.style.strokeColor) || fill;
		let el;
		if (dec.shape === 'Circle') { el = document.createElementNS(ns, 'circle'); el.setAttribute('cx', dir.to.x); el.setAttribute('cy', dir.to.y); el.setAttribute('r', w / 2); }
		else {
			el = document.createElementNS(ns, 'polygon');
			const tip = dir.to, bx = tip.x - Math.cos(ang) * w, by = tip.y - Math.sin(ang) * w;
			const px = -Math.sin(ang) * hgt / 2, py = Math.cos(ang) * hgt / 2;
			el.setAttribute('points', `${tip.x},${tip.y} ${bx + px},${by + py} ${bx - px},${by - py}`);
		}
		el.setAttribute('fill', fill); el.setAttribute('stroke', sc); el.setAttribute('class', 'dfc-diagram-decorator');
		return el;
	}
	_polyPoint(pts, t) {
		const segs = []; let total = 0;
		for (let i = 1; i < pts.length; i++) { const l = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y); segs.push(l); total += l; }
		let target = total * t;
		for (let i = 0; i < segs.length; i++) {
			if (target <= segs[i] || i === segs.length - 1) { const k = segs[i] ? target / segs[i] : 0; return { x: pts[i].x + (pts[i + 1].x - pts[i].x) * k, y: pts[i].y + (pts[i + 1].y - pts[i].y) * k }; }
			target -= segs[i];
		}
		return pts[0];
	}
	_bezierPoint(p0, c1, c2, p1, t) {
		const u = 1 - t;
		return { x: u * u * u * p0.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t * t * t * p1.x, y: u * u * u * p0.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t * t * t * p1.y };
	}
	/** Redraws only the connectors (after a node moved). */
	_redrawEdges() {
		this.edgeLayer.innerHTML = '';
		[...this.nodeLayer.querySelectorAll('.dfc-diagram-edge-label')].forEach((l) => l.remove());
		this._connectors.forEach((e) => this._renderConnector(e));
	}

	// ------------------------------------------------------------ view: pan, zoom, fit
	_applyTransform() { this.viewport.style.transform = `translate(${this._tx}px, ${this._ty}px) scale(${this._zoom})`; }
	get zoom() { return this._zoom; }
	_setZoom(z, cx, cy) {
		const o = this.options;
		const nz = Math.min(o.maxZoom, Math.max(o.minZoom, z));
		if (cx === undefined) { const r = this.el.getBoundingClientRect(); cx = r.width / 2; cy = r.height / 2; }
		const k = nz / this._zoom;
		this._tx = cx - (cx - this._tx) * k; this._ty = cy - (cy - this._ty) * k; this._zoom = nz;
		this._applyTransform();
		this.emit('zoom', { zoom: nz });
		return this;
	}
	zoomIn(factor = 0.2) { return this._setZoom(this._zoom * (1 + factor)); }
	zoomOut(factor = 0.2) { return this._setZoom(this._zoom / (1 + factor)); }
	/** zoomTo(1.5) or zoomTo({ type: 'ZoomIn' | 'ZoomOut', zoomFactor }). */
	zoomTo(arg) {
		if (typeof arg === 'number') return this._setZoom(arg);
		const f = (arg && arg.zoomFactor) || 0.2;
		return arg && arg.type === 'ZoomOut' ? this.zoomOut(f) : this.zoomIn(f);
	}
	/** Scales and centres the whole diagram in the view (never above 100%). */
	fitToPage(arg) {
		const pad = arg && arg.margin ? arg.margin : { left: 20, top: 20, right: 20, bottom: 20 };
		const r = this.el.getBoundingClientRect();
		const c = this._content || { w: 0, h: 0 };
		if (!c.w || !c.h || !r.width || !r.height) return this;
		const z = Math.min(1, (r.width - pad.left - pad.right) / c.w, (r.height - pad.top - pad.bottom) / c.h);
		this._zoom = Math.max(this.options.minZoom, z);
		this._tx = (r.width - c.w * this._zoom) / 2; this._ty = (r.height - c.h * this._zoom) / 2;
		this._applyTransform();
		this.emit('zoom', { zoom: this._zoom });
		return this;
	}
	/** Centres the view on a node id or a diagram point { x, y }. */
	bringToCenter(target) {
		let n = typeof target === 'string' ? this._byId.get(target) : target;
		if (n && n.x === undefined && n.offsetX !== undefined) n = { x: n.offsetX, y: n.offsetY };
		if (!n || n.x === undefined) return this;
		const r = this.el.getBoundingClientRect();
		this._tx = r.width / 2 - n.x * this._zoom; this._ty = r.height / 2 - n.y * this._zoom;
		this._applyTransform();
		return this;
	}
	bringIntoView(id) {
		const n = this._byId.get(id); if (!n) return this;
		const r = this.el.getBoundingClientRect();
		const left = this._tx + (n.x - n.width / 2) * this._zoom, top = this._ty + (n.y - n.height / 2) * this._zoom;
		const right = left + n.width * this._zoom, bottom = top + n.height * this._zoom;
		if (left < 0 || right > r.width || top < 0 || bottom > r.height) this.bringToCenter(n);
		return this;
	}

	// ------------------------------------------------------------ events
	bind() {
		const it = this._interaction();
		// Callback-style options (click: fn, ...) are wired as listeners so hosts can pass handlers inline.
		['click', 'doubleClick', 'selectionChange', 'textEdit', 'nodeAdded', 'nodeRemoved', 'nodeMoved', 'zoom', 'valueChanged', 'created'].forEach((name) => { if (typeof this.options[name] === 'function') this.on(name, this.options[name]); });
		setTimeout(() => { if (!this.destroyed) this.emit('created', {}); }, 0);
		this._onWheel = (e) => {
			if (!it.zoom) return;
			e.preventDefault();
			const r = this.el.getBoundingClientRect();
			this._setZoom(this._zoom * Math.exp(-e.deltaY * 0.0015), e.clientX - r.left, e.clientY - r.top);
		};
		this._onDown = (e) => {
			if (e.button !== 0 || (e.target.closest && e.target.closest('.dfc-diagram-toolbar, .dfc-diagram-handle, [contenteditable="true"]'))) return;
			if (this._editing && this._editing.contains(e.target)) return;
			const nodeEl = e.target.closest && e.target.closest('.dfc-diagram-node');
			this._drag = { x: e.clientX, y: e.clientY, tx: this._tx, ty: this._ty, moved: false, node: nodeEl && it.drag ? this._byId.get(nodeEl.dataset.id) : null, target: e.target };
			if (this._drag.node) { this._drag.nx = this._drag.node.x; this._drag.ny = this._drag.node.y; }
			try { if (this.el.setPointerCapture) this.el.setPointerCapture(e.pointerId); } catch (err) { /* synthetic events have no active pointer */ }
		};
		this._onMove = (e) => {
			const d = this._drag; if (!d) return;
			const dx = e.clientX - d.x, dy = e.clientY - d.y;
			if (!d.moved && Math.hypot(dx, dy) < 4) return;
			d.moved = true;
			if (d.node) { d.node.x = d.nx + dx / this._zoom; d.node.y = d.ny + dy / this._zoom; d.node.el.style.left = (d.node.x - d.node.width / 2) + 'px'; d.node.el.style.top = (d.node.y - d.node.height / 2) + 'px'; this._redrawEdges(); return; }
			if (!it.pan) return;
			this._tx = d.tx + dx; this._ty = d.ty + dy; this._applyTransform();
		};
		this._onUp = (e) => {
			const d = this._drag; if (!d) return; this._drag = null;
			if (d.moved) { if (d.node) { this.emit('nodeMoved', { node: this._public(d.node) }); this._valueChanged(); } return; }
			const nodeEl = d.target.closest && d.target.closest('.dfc-diagram-node');
			const edgeEl = d.target.closest && d.target.closest('.dfc-diagram-edge');
			const node = nodeEl ? this._byId.get(nodeEl.dataset.id) : null;
			const edge = edgeEl ? this._connectors.find((c) => c.id === edgeEl.getAttribute('data-id')) : null;
			if (it.select || it.edit) { const wasSelected = node && this._selected === node.id; if (node) this.select(node.id); else this.clearSelection(); if (wasSelected && it.edit && !e.target.isContentEditable) { this.startTextEdit(node.id); return; } }
			this.emit('click', { node: node ? this._public(node) : null, connector: edge ? this._public(edge) : null, event: e, element: node ? this._public(node) : (edge ? this._public(edge) : null) });
		};
		this._onDbl = (e) => {
			const nodeEl = e.target.closest && e.target.closest('.dfc-diagram-node');
			const node = nodeEl ? this._byId.get(nodeEl.dataset.id) : null;
			if (node && it.edit) { this.startTextEdit(node.id); return; }
			this.emit('doubleClick', { node: node ? this._public(node) : null, event: e, source: node ? this._public(node) : null });
		};
		this._onKey = (e) => {
			if (!it.edit || !this._selected || e.target.isContentEditable) return;
			if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); this.removeSubtree(this._selected); }
			if (e.key === 'F2' || e.key === 'Enter') { e.preventDefault(); this.startTextEdit(this._selected); }
		};
		this.el.addEventListener('wheel', this._onWheel, { passive: false });
		this.el.addEventListener('pointerdown', this._onDown);
		this.el.addEventListener('pointermove', this._onMove);
		this.el.addEventListener('pointerup', this._onUp);
		this.el.addEventListener('pointercancel', this._onUp);
		this.el.addEventListener('dblclick', this._onDbl);
		this._onContext = (e) => {
			const o = this.options;
			if (!o.contextMenu) return;
			const nodeEl = e.target.closest && e.target.closest('.dfc-diagram-node');
			const edgeEl = e.target.closest && e.target.closest('.dfc-diagram-edge');
			const node = nodeEl ? this._byId.get(nodeEl.dataset.id) : null;
			const edge = edgeEl ? this._connectors.find((c) => c.id === edgeEl.getAttribute('data-id')) : null;
			if (e.target.isContentEditable) return;
			const ctx = { node: node ? this._public(node) : null, connector: edge ? this._public(edge) : null, diagram: this, event: e };
			ctx.defaults = node ? this._defaultMenuItems(node) : [];
			let items = o.contextMenu === true ? ctx.defaults : (typeof o.contextMenu === 'function' ? o.contextMenu(ctx) : o.contextMenu);
			items = (items || []).filter(Boolean);
			if (!items.length) return;
			e.preventDefault();
			if (node && (it.select || it.edit)) this.select(node.id);
			this.menu = this.menu || new ContextMenu({ host: document.body });
			this.menu.show({ x: e.clientX, y: e.clientY, items, ctx, returnFocus: this.el });
		};
		this.el.addEventListener('contextmenu', this._onContext);
		this.el.addEventListener('keydown', this._onKey);
		this.el.tabIndex = 0;
		this._ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => { if (this.options.fitOnRender && !this._userZoomed) this.fitToPage(); }) : null;
		if (this._ro) this._ro.observe(this.el);
		this.on('zoom', () => { /* fit() also emits; mark user zoom only from wheel/buttons */ });
		const markUser = () => { this._userZoomed = true; };
		this.el.addEventListener('wheel', markUser, { passive: true });
		if (this.toolbarEl) this.toolbarEl.addEventListener('click', markUser);
	}
	unbind() { if (this._ro) this._ro.disconnect(); if (this.menu) this.menu.destroy(); }

	/** The built-in node menu: rename, colour, add and delete (editing items only in edit mode). */
	_defaultMenuItems(n) {
		const it = this._interaction();
		const items = [];
		if (!it.edit) return items;
		const lay = this.options.layout || {};
		const isMind = lay.type === 'MindMap';
		const branch = n.branch || (n.data && n.data.branch) || 'Root';
		items.push({ name: 'Rename', icon: 'pencil', shortcut: 'F2', action: () => this.startTextEdit(n.id) });
		items.push({ name: 'Colour', swatches: this.options.colours, selected: n.style && n.style.fill, action: (ctx, colour) => this.setNodeStyle(n.id, { fill: colour }) });
		items.push('separator');
		if (isMind) {
			if (branch === 'Root' || /right/i.test(branch)) items.push({ name: 'Add topic to the right', icon: 'plus', action: () => this.addChild(n.id, { branch: 'Right' }) });
			if (branch === 'Root' || /left/i.test(branch)) items.push({ name: 'Add topic to the left', icon: 'plus', action: () => this.addChild(n.id, { branch: 'Left' }) });
		} else items.push({ name: 'Add child', icon: 'plus', action: () => this.addChild(n.id) });
		if (this._connectors.some((c) => c.targetID === n.id) || !isMind) items.push({ name: 'Delete', icon: 'trash', shortcut: 'Del', cssClass: 'is-danger', action: () => this.removeSubtree(n.id) });
		return items;
	}
	/** Merges style properties into a node (fill, strokeColor, ...), re-renders it and records fill on its data so a refresh keeps it. */
	setNodeStyle(id, patch) {
		const n = this._byId.get(id); if (!n) return this;
		Object.assign(n.style, patch || {});
		if (patch && patch.fill !== undefined && n.data && typeof n.data === 'object') n.data.fill = patch.fill;
		if (n.el) { const fresh = this._renderNode(n); n.el.replaceWith(fresh); n.el = fresh; if (this._selected === id) this._applySelection(); }
		this.emit('nodeChanged', { node: this._public(n) });
		this._valueChanged();
		return this;
	}

	// ------------------------------------------------------------ selection + editing
	select(id) {
		if (this._selected === id) return this;
		this._selected = id;
		this._applySelection();
		this.emit('selectionChange', { node: this._byId.has(id) ? this._public(this._byId.get(id)) : null });
		return this;
	}
	clearSelection() { if (!this._selected) return this; this._selected = null; this._applySelection(); this.emit('selectionChange', { node: null }); return this; }
	get selected() { return this._selected && this._byId.has(this._selected) ? this._public(this._byId.get(this._selected)) : null; }
	_applySelection() {
		[...this.nodeLayer.querySelectorAll('.dfc-diagram-node.is-selected')].forEach((el) => el.classList.remove('is-selected'));
		if (this.handles) { this.handles.remove(); this.handles = null; }
		const n = this._selected && this._byId.get(this._selected);
		if (!n || !n.el) return;
		n.el.classList.add('is-selected');
		if (!this._interaction().edit) return;
		const lay = this.options.layout || {};
		const isMind = lay.type === 'MindMap';
		const branch = n.branch || (n.data && n.data.branch) || 'Root';
		const hs = [];
		const show = this.options.handles || [];
		const mk = (title, icon, side, fn) => h('button', { type: 'button', className: 'dfc-diagram-handle is-' + side, title, 'aria-label': title, onMousedown: (e) => e.stopPropagation(), onPointerdown: (e) => e.stopPropagation(), onClick: (e) => { e.stopPropagation(); fn(); } }, renderIcon(icon));
		if (show.includes('add')) {
			if (isMind) {
				if (branch === 'Root' || /right/i.test(branch)) hs.push(mk('Add topic to the right', 'plus', 'right', () => this.addChild(n.id, { branch: 'Right' })));
				if (branch === 'Root' || /left/i.test(branch)) hs.push(mk('Add topic to the left', 'plus', 'left', () => this.addChild(n.id, { branch: 'Left' })));
			} else hs.push(mk('Add child', 'plus', this._vertical ? 'bottom' : 'right', () => this.addChild(n.id)));
		}
		if (show.includes('delete') && (branch !== 'Root' || !isMind)) { if (this._connectors.some((c) => c.targetID === n.id)) hs.push(mk('Delete', 'trash', 'top', () => this.removeSubtree(n.id))); }
		if (!hs.length) return;
		this.handles = h('div', { className: 'dfc-diagram-handles' }, hs);
		n.el.appendChild(this.handles);
	}
	/** Adds a child node under parentId (with a connector), lays out again and starts editing its label. */
	addChild(parentId, opts = {}) {
		const parent = this._byId.get(parentId); if (!parent) return null;
		const id = opts.id || rid('node');
		const label = opts.label || 'New';
		const data = Object.assign({}, opts.data || {}, { id, parentId, Label: label });
		if (opts.branch) data.branch = /right/i.test(opts.branch) ? (parent.branch === 'Root' ? 'Right' : 'subRight') : (parent.branch === 'Root' ? 'Left' : 'subLeft');
		const nodeRaw = Object.assign({ id, data, annotations: [{ content: label }] }, opts.node || {});
		const connRaw = Object.assign({ id: 'e-' + parentId + '-' + id, sourceID: parentId, targetID: id }, opts.connector || {});
		const node = this._makeNode(nodeRaw); const conn = this._makeConnector(connRaw);
		this._nodes.push(node); this._byId.set(node.id, node); this._connectors.push(conn);
		this.doLayout(!this._userZoomed);
		this.select(node.id); this.bringIntoView(node.id);
		this.emit('nodeAdded', { node: this._public(node), connector: this._public(conn) });
		this._valueChanged();
		if (opts.edit !== false && this._interaction().edit) this.startTextEdit(node.id);
		return this._public(node);
	}
	/** Adds a node or a connector (detected by sourceID/targetID). */
	add(item) {
		if (item && item.sourceID !== undefined) { const c = this._makeConnector(item); if (this._byId.has(c.sourceID) && this._byId.has(c.targetID)) this._connectors.push(c); this.doLayout(false); this._valueChanged(); return this._public(c); }
		const n = this._makeNode(item); this._nodes.push(n); this._byId.set(n.id, n); this.doLayout(false); this._valueChanged(); return this._public(n);
	}
	remove(id) {
		const before = this._nodes.length + this._connectors.length;
		this._nodes = this._nodes.filter((n) => n.id !== id); this._byId.delete(id);
		this._connectors = this._connectors.filter((c) => c.id !== id && c.sourceID !== id && c.targetID !== id);
		if (this._selected === id) this._selected = null;
		if (before !== this._nodes.length + this._connectors.length) { this.doLayout(false); this.emit('nodeRemoved', { ids: [id] }); this._valueChanged(); }
		return this;
	}
	/** Removes a node and everything below it. */
	removeSubtree(id) {
		const ids = []; const walk = (x) => { if (ids.includes(x)) return; ids.push(x); this._connectors.filter((c) => c.sourceID === x).forEach((c) => walk(c.targetID)); };
		walk(id);
		this._nodes = this._nodes.filter((n) => !ids.includes(n.id)); ids.forEach((x) => this._byId.delete(x));
		this._connectors = this._connectors.filter((c) => !ids.includes(c.sourceID) && !ids.includes(c.targetID));
		if (ids.includes(this._selected)) this._selected = null;
		this.doLayout(!this._userZoomed); this.emit('nodeRemoved', { ids }); this._valueChanged();
		return this;
	}
	/** Inline edit of the node's first annotation. */
	startTextEdit(id) {
		const n = this._byId.get(id); if (!n || !n.el) return this;
		let label = n.el.querySelector('.dfc-diagram-label.is-primary');
		if (!label) { if (!n.annotations.length) n.annotations.push({ content: '' }); label = this._renderAnnotation(n, n.annotations[0], 0); n.el.appendChild(label); }
		if (this._editing) this._editing.blur();
		this._editing = label;
		// Annotation colours are inline (often white on a coloured node); neutralise them while editing so the text is readable on the edit box.
		label.dataset.editColor = label.style.color || ''; label.dataset.editBg = label.style.background || '';
		label.style.color = ''; label.style.background = '';
		label.contentEditable = 'true'; label.classList.add('is-editing'); n.el.classList.add('is-editing-node'); label.focus();
		const range = document.createRange(); range.selectNodeContents(label); const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range);
		const finish = (commit) => {
			label.contentEditable = 'false'; label.classList.remove('is-editing'); n.el.classList.remove('is-editing-node'); this._editing = null;
			label.style.color = label.dataset.editColor || ''; label.style.background = label.dataset.editBg || ''; delete label.dataset.editColor; delete label.dataset.editBg;
			label.removeEventListener('blur', onBlur); label.removeEventListener('keydown', onKey);
			if (!commit) { label.innerHTML = escapeHtml(String(n.annotations[0].content || '')).replace(/\n/g, '<br>'); return; }
			const text = label.innerText.replace(/\n+$/, '');
			n.annotations[0].content = text;
			if (n.data && typeof n.data === 'object') { if ('Label' in n.data || !('label' in n.data)) n.data.Label = text; if ('label' in n.data) n.data.label = text; }
			label.innerHTML = escapeHtml(text).replace(/\n/g, '<br>');
			this.emit('textEdit', { node: this._public(n), text }); this._valueChanged();
		};
		const onBlur = () => finish(true);
		const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); label.blur(); } if (e.key === 'Escape') { e.preventDefault(); label.removeEventListener('blur', onBlur); finish(false); } e.stopPropagation(); };
		label.addEventListener('blur', onBlur); label.addEventListener('keydown', onKey);
		return this;
	}

	// ------------------------------------------------------------ data access
	_public(item) {
		const out = {};
		Object.keys(item).forEach((k) => { if (k !== 'el') out[k] = item[k]; });
		if (item.x !== undefined) { out.offsetX = item.x; out.offsetY = item.y; }
		return out;
	}
	get nodes() { return this._nodes.map((n) => this._public(n)); }
	get connectors() { return this._connectors.map((c) => this._public(c)); }
	getObject(id) { const n = this._byId.get(id); if (n) return this._public(n); const c = this._connectors.find((x) => x.id === id); return c ? this._public(c) : null; }
	/** Replaces the data and lays out again. */
	setData(data) { this.options.value = null; this.options.dataSourceSettings = null; this.options.nodes = (data && data.nodes) || []; this.options.connectors = (data && data.connectors) || []; this._selected = null; this._build(); this.doLayout(true); return this; }
	/** Re-applies node and connector defaults to the current graph (including items added since load) and lays out again. */
	refresh() {
		const cur = this.value;
		this.options.value = null; this.options.dataSourceSettings = null;
		this.options.nodes = cur.nodes; this.options.connectors = cur.connectors;
		this._build();
		this.doLayout(!this._userZoomed);
		return this;
	}
	get value() { return { nodes: this.nodes, connectors: this.connectors }; }
	set value(v) { this.setData(v || {}); this._valueChanged(); }
	toJSON() { return this.value; }
	_valueChanged() { this.emit('valueChanged', { value: this.value, source: 'user' }); }

	/** The whole diagram as a standalone SVG string (nodes rendered as foreignObject HTML). */
	toSvg() {
		const c = this._content || { w: 0, h: 0 };
		const nodesHtml = [...this.nodeLayer.children].map((el) => `<foreignObject x="${parseFloat(el.style.left) || 0}" y="${parseFloat(el.style.top) || 0}" width="${el.offsetWidth}" height="${el.offsetHeight}"><div xmlns="http://www.w3.org/1999/xhtml">${el.outerHTML}</div></foreignObject>`).join('');
		return `<svg xmlns="http://www.w3.org/2000/svg" width="${c.w}" height="${c.h}" viewBox="0 0 ${c.w} ${c.h}">${this.edgeLayer.innerHTML}${nodesHtml}</svg>`;
	}
}

register('diagram', Diagram);
registerFieldType('diagram', {
	create: (container, field, value) => new Diagram(container, Object.assign({ value: value || { nodes: [], connectors: [] } }, field.diagram || {})),
	getValue: (inst) => inst.value,
	setValue: (inst, v) => { inst.value = v; },
});
